<template>
  <div class="flex flex-col gap-3 min-w-full md:min-w-[400px]">
    <div
      v-for="stars in starLevels"
      :key="stars"
      :class="[
        'flex items-center gap-3 group',
        getCount(stars) >= 1
          ? 'cursor-pointer'
          : 'cursor-default pointer-events-none',
      ]"
      @click="getCount(stars) >= 1 && $emit('click:stars', stars)"
    >
      <span
        :class="[
          'text-xs min-w-[60px]',
          getCount(stars) >= 1 ? 'text-black font-semibold' : 'text-gray-400',
        ]"
      >
        {{ t('review.breakdown.starsLabel', stars) }}
      </span>
      <div class="flex-1 min-w-0 h-2 rounded-full overflow-hidden relative">
        <div class="absolute inset-0 bg-gray-200 rounded-full" />
        <div
          class="absolute inset-y-0 left-0 top-0 h-full bg-lime-600 rounded-full transition-all duration-300"
          :style="{ width: `${getPercentage(stars)}%` }"
        />
        <div
          v-if="getCount(stars) >= 1"
          class="absolute inset-0 rounded-full bg-[#F4F0EA]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
        />
      </div>
      <span
        :class="[
          'text-xs text-right tabular-nums',
          getCount(stars) >= 1 ? 'text-gray-600' : 'text-gray-400',
        ]"
      >
        {{ formatCount(getCount(stars)) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ReviewBreakdown } from '~/composables/types/event'

const { t, locale } = useI18n()

const props = defineProps<{
  breakdown: ReviewBreakdown
}>()

const starLevels = [5, 4, 3, 2, 1] as const

const totalReviews = computed(() => {
  const { breakdown } = props
  return (
    breakdown['1_star'] +
    breakdown['2_star'] +
    breakdown['3_star'] +
    breakdown['4_star'] +
    breakdown['5_star']
  )
})

const getCount = (stars: number): number => {
  const key = `${stars}_star` as keyof ReviewBreakdown
  return props.breakdown[key] ?? 0
}

const getPercentage = (stars: number): number => {
  const total = totalReviews.value
  if (total === 0) return 0
  return (getCount(stars) / total) * 100
}

const formatCount = (count: number): string => {
  return new Intl.NumberFormat(locale.value).format(count)
}
</script>
